import { Flex } from "@zenchef/styled-system/jsx";
import BaseDatePicker, {
  DatePickerProps as BaseDatePickerProps,
} from "../SelectionControls/DatePicker/DatePicker";
import { DatePickerRootProps } from "../SelectionControls/DatePicker/Root";
import { Translations } from "../SelectionControls/DatePicker/types";
import Input, { InputProps } from "./Input";
import { DatePicker as ArkUiDatePicker } from "@ark-ui/react";
import {
  aromaDatepickerInput,
  AromaDatepickerInputRecipe,
  AromaDatepickerInputVariantProps,
} from "@zenchef/styled-system/recipes";
import { Text } from "../typography/Text";
import Icon from "../Icon/Icon";
import mapStringToDateValue from "../SelectionControls/DatePicker/functions/mapStringToDateValue";
import { Field } from "../..";
import { useState } from "react";
import { ValueChangeDetails } from "@ark-ui/react/dist/components/date-picker/date-picker";
import { padDateValue } from "../../utils/padDateValue";

interface DatePickerInputProps extends InputProps {
  classes: ReturnType<AromaDatepickerInputRecipe>;
  hiddenIcon?: boolean;
  open?: boolean;
}

function DatePickerInput({
  classes,
  hiddenIcon,
  ...props
}: DatePickerInputProps) {
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);

  return (
    <Input
      iconLeftName={!hiddenIcon ? "calendar" : undefined}
      iconRight
      className={isTriggerHovered ? "trigger-hovered" : ""}
      icon={
        !hiddenIcon ? (
          <ArkUiDatePicker.Trigger
            disabled={props.disabled}
            className={classes.trigger}
            onMouseEnter={() => setIsTriggerHovered(true)}
            onMouseLeave={() => setIsTriggerHovered(false)}
          >
            <Icon name={props.open ? "chevron-up" : "chevron-down"} />
          </ArkUiDatePicker.Trigger>
        ) : undefined
      }
      {...props}
    />
  );
}

interface DatePickerProps
  extends Omit<
      DatePickerRootProps,
      | "translations"
      | "onOpenChange"
      | "open"
      | "value"
      | "defaultValue"
      | "focusedValue"
    >,
    Pick<BaseDatePickerProps, "value" | "defaultValue" | "focusedValue"> {
  translations: Translations;
  open: NonNullable<BaseDatePickerProps["open"]>;
  onOpenChange: NonNullable<BaseDatePickerProps["onOpenChange"]>;
}

interface BaseProps
  extends InputProps,
    AromaDatepickerInputVariantProps,
    Pick<DatePickerInputProps, "hiddenIcon"> {
  datePicker: DatePickerProps;
  rootClassName?: string;
  zIndex?: string;
  mode?: "single" | "range";
  labelFrom?: string;
  labelTo?: string;
}

// Based on the variant.inblock = false in the aromaDatepickerInput recipe
interface InlineProps extends BaseProps {
  labelFrom?: never;
  labelTo?: never;
}

// Based on the variant.inblock = true in the aromaDatepickerInput recipe
interface InblockProps extends BaseProps {
  labelFrom?: string;
  labelTo?: string;
}

export type Props = InlineProps | InblockProps;

export default function DatePicker({
  datePicker,
  rootClassName,
  labelFrom,
  labelTo,
  ...propsWithVariants
}: Props) {
  const [variants] = aromaDatepickerInput.splitVariantProps(propsWithVariants);
  const classes = aromaDatepickerInput(variants);

  const {
    value: valueString,
    defaultValue: defaultValueString,
    focusedValue: focusedValueString,
    onOpenChange,
    ...datePickerProps
  } = datePicker;

  const { value, defaultValue, focusedValue } = mapStringToDateValue({
    value: valueString,
    defaultValue: defaultValueString,
    focusedValue: focusedValueString,
  });

  const handleChange = (value: ValueChangeDetails) => {
    const copyValue = {
      ...value,
      valueAsString: value.value.map(
        (date) =>
          `${date.year}-${padDateValue(date.month)}-${padDateValue(date.day)}`,
      ),
    };

    return datePickerProps?.onValueChange?.(copyValue);
  };

  const commonDatePickerProps: DatePickerRootProps = {
    ...datePickerProps,
    className: rootClassName,
    value,
    defaultValue,
    focusedValue,
    onOpenChange,
    translations: undefined,
    startOfWeek: datePicker.startOfWeek ?? 1,
    closeOnSelect: datePicker.closeOnSelect ?? true,
    onValueChange: handleChange,
  };

  if (variants.inblock) {
    return (
      <BaseDatePicker.Root {...commonDatePickerProps}>
        <ArkUiDatePicker.Control>
          <Flex className={classes.rangeContainer}>
            <Field
              width="100%"
              label={labelFrom}
              children={
                <ArkUiDatePicker.Input index={0} asChild>
                  <DatePickerInput
                    {...propsWithVariants}
                    open={commonDatePickerProps.open}
                    classes={classes}
                  />
                </ArkUiDatePicker.Input>
              }
            />
            {datePickerProps.selectionMode === "range" ? (
              <Field
                width="100%"
                label={labelTo}
                children={
                  <ArkUiDatePicker.Input index={1} asChild>
                    <DatePickerInput
                      {...propsWithVariants}
                      open={commonDatePickerProps.open}
                      classes={classes}
                    />
                  </ArkUiDatePicker.Input>
                }
              />
            ) : null}
          </Flex>
        </ArkUiDatePicker.Control>
        <BaseDatePicker.Portal
          translations={datePickerProps.translations}
          defaultValue={defaultValue}
          withContainer
        />
      </BaseDatePicker.Root>
    );
  }

  return (
    <BaseDatePicker.Root {...commonDatePickerProps}>
      <ArkUiDatePicker.Control>
        <Flex className={classes.rangeContainer}>
          <ArkUiDatePicker.Input index={0} asChild>
            <DatePickerInput
              {...propsWithVariants}
              open={commonDatePickerProps.open}
              classes={classes}
            />
          </ArkUiDatePicker.Input>
          {datePickerProps.selectionMode === "range" ? (
            <>
              <Text
                textStyle="paragraph.m.regular"
                color="border.neutral.bolder"
              >
                -
              </Text>
              <ArkUiDatePicker.Input index={1} asChild>
                <DatePickerInput
                  {...propsWithVariants}
                  open={commonDatePickerProps.open}
                  classes={classes}
                />
              </ArkUiDatePicker.Input>
            </>
          ) : null}
        </Flex>
      </ArkUiDatePicker.Control>
      <BaseDatePicker.Portal
        zIndex={propsWithVariants?.zIndex}
        translations={datePickerProps.translations}
        defaultValue={defaultValue}
        withContainer
      />
    </BaseDatePicker.Root>
  );
}
